import { Link, useLocation, useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import {
  useQueryApplication,
  useQueryEventsByApplication,
  useQueryTags,
} from "hooks/events";
import { EventForm } from "components/events/EventForm/EventFormMain";
import { useAppTranslation } from "hooks/useTranslation";
import { useQueryVenues } from "hooks/venues";
import { useQueryOrganizers } from "hooks/organizers";
import { Alert, Anchor, Flex, Image, List, Stack, Text } from "@mantine/core";
import {
  IconAlertTriangle,
  IconArchive,
  IconCircleCheck,
  IconClock,
  IconInfoCircle,
} from "@tabler/icons-react";
import { franc } from "franc-min";
import dayjs from "dayjs";
import { isPdfUrl, pdfToImageBlob } from "utils/pdf";
import { Database } from "types/supabase";
import { readImportMetadata } from "./importMetadata";

type ApplicationRow = Database["public"]["Tables"]["applications"]["Row"];

/**
 * Router state is history, not a database row: any page that can push history
 * can write it. Narrowed to the one member this page reads, so the eight
 * fields that never existed as columns stop compiling.
 */
const readApplicationFromState = (state: unknown): ApplicationRow | null =>
  (state as { application?: ApplicationRow } | null)?.application ?? null;

/**
 * PostgREST returns a distinct code when a single-row read matched no rows —
 * which is what a deep link to a deleted application produces. Read
 * defensively: the hook's error is typed `Error`, and nothing on that type
 * guarantees a `code` member at all.
 */
const readErrorCode = (error: unknown): string | null => {
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === "string" ? code : null;
};

const formatDateTime = (value: string | null): string => {
  if (!value) return "";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY HH:mm") : value;
};

const isBlank = (value: string | null): boolean =>
  value === null || value.trim() === "";

/**
 * Allowlist, not a blocklist. A blocklist of "javascript:" and "data:" loses to
 * whitespace, casing and control characters; a prefix test on the one scheme we
 * accept cannot. Shared by the source link and by the captured screenshot: both
 * carry a URL this page did not author into an element that acts on it, and the
 * click or the load happens inside an authenticated admin session.
 */
const isHttpsUrl = (value: string | null): boolean =>
  typeof value === "string" && value.startsWith("https://");

export const ApplicationEdit = () => {
  const { id } = useParams();
  const location = useLocation();
  const { t } = useAppTranslation();
  const applicationFromState = readApplicationFromState(location.state);
  const [application, setApplication] = useState<ApplicationRow | null>(
    applicationFromState
  );
  const [pictureUrl, setPictureUrl] = useState<string | null>(null);
  const [isConvertingPdf, setIsConvertingPdf] = useState(false);
  const convertedBlobUrlRef = useRef<string | null>(null);

  // The list pushes the row it already has into history, and the query is
  // normally skipped for it — a fetch for a row we are holding is waste.
  //
  // A `pending` snapshot is the exception, and it is why the poll exists at
  // all: history is a snapshot, not a subscription, and a pending one is
  // guaranteed to go stale within seconds. Skipping the query for it would
  // leave every row opened from the list — which is every ordinary click —
  // saying "pending" for ever, however good the polling underneath.
  const isPendingFromState = applicationFromState?.import_status === "pending";
  const {
    data: fetchedApplication,
    isLoading: isLoadingApplication,
    isError: isApplicationError,
    error: applicationError,
  } = useQueryApplication(
    parseInt(id || "0"),
    !applicationFromState || isPendingFromState
  );

  // Required queries for EventForm
  const { venues } = useQueryVenues({});
  const { organizers } = useQueryOrganizers({
    page: 1,
    perPage: 1000,
  });
  const { tags } = useQueryTags();

  // The events this application already produced. Skipped entirely while the
  // application is unprocessed — by definition it has produced nothing.
  const { events: createdEvents } = useQueryEventsByApplication(
    application?.id ?? 0,
    !!application?.processed
  );

  // Adopt a fetched row when there is none yet — and also whenever the one on
  // screen is still `pending`, which is the whole point of polling it. Without
  // the second clause the first stale snapshot is the last thing this page ever
  // renders. Setting state to the identical object reference is a no-op in
  // React, and react-query's structural sharing keeps that reference stable
  // across a poll that returns an unchanged row, so this does not loop.
  useEffect(() => {
    if (!fetchedApplication) return;
    if (!application || application.import_status === "pending") {
      setApplication(fetchedApplication);
    }
  }, [fetchedApplication, application]);

  useEffect(() => {
    const sourceUrl = application?.main_picture;
    if (!sourceUrl) {
      setPictureUrl("");
      return;
    }

    let cancelled = false;
    setIsConvertingPdf(true);

    (async () => {
      try {
        if (await isPdfUrl(sourceUrl)) {
          const blob = await pdfToImageBlob(sourceUrl);
          if (cancelled) return;
          const blobUrl = URL.createObjectURL(blob);
          convertedBlobUrlRef.current = blobUrl;
          setPictureUrl(blobUrl);
        } else if (!cancelled) {
          setPictureUrl(sourceUrl);
        }
      } catch (err) {
        console.error("Failed to convert PDF to image:", err);
        if (!cancelled) setPictureUrl(sourceUrl);
      } finally {
        if (!cancelled) setIsConvertingPdf(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [application?.main_picture]);

  useEffect(() => {
    return () => {
      if (convertedBlobUrlRef.current) {
        URL.revokeObjectURL(convertedBlobUrlRef.current);
      }
    };
  }, []);

  // Detect language using franc
  const detectLanguage = (text: string) => {
    // Call franc with the text to get language code
    if (!text || text.trim().length < 10) return null; // Need enough text for reliable detection
    const langCode = franc(text);
    if (langCode === "por") return "portuguese";
    if (langCode === "eng") return "english";
    return null; // Inconclusive or other language
  };

  // Transform application to event format for EventForm
  const constructEventFromApplication = () => {
    if (!application) return null;

    // Determine language primarily from description, fall back to title if needed
    const description = application.event_description || "";
    const title = application.event_title || "";

    let detectedLanguage = detectLanguage(description);

    // If description language detection is inconclusive, check title
    if (!detectedLanguage) {
      detectedLanguage = detectLanguage(title);
    }

    // Default to English if still inconclusive
    if (!detectedLanguage) {
      detectedLanguage = "english";
    }

    const isPortuguese = detectedLanguage === "portuguese";

    // Find matching tag ID based on application category
    let mainTagId = null;
    if (application.category && tags) {
      // Look for a tag matching "{tag} / {tag_pt}" format that includes the category
      const matchingTag = tags.find(
        (tag) =>
          (tag.tag &&
            tag.tag_pt &&
            `${tag.tag} / ${tag.tag_pt}` === application.category) ||
          tag.tag === application.category ||
          tag.tag_pt === application.category
      );
      if (matchingTag) {
        mainTagId = matchingTag.id;
      }
    }

    // FBIMP-12: a datetime is built only when BOTH halves are present.
    // `new Date("2026-09-10")` is a valid midnight UTC that renders exactly
    // like a curated time, so falling through to a bare date fabricates a
    // start time the source never stated — the same defect as the 10:00
    // default, one layer up, and it would defeat the empty date controls.
    const endDate = application.ending_date || application.starting_date;
    const startTime =
      application.starting_date && application.starting_time
        ? `${application.starting_date}T${application.starting_time}+00:00`
        : null;
    const finishTime =
      endDate && application.ending_time
        ? `${endDate}T${application.ending_time}+00:00`
        : null;

    // Map application fields to event fields based on detected language.
    // NOTE: no `id` and no `uid`. The application id reaches the form as
    // `applicationContext.id`; overloading `event.id` with it made an
    // application id load-bearing as an event id.
    return {
      title: isPortuguese ? "" : application.event_title || "",
      title_pt: isPortuguese ? application.event_title || "" : "",
      sub_title: isPortuguese ? "" : application.subtitle || "",
      sub_title_pt: isPortuguese ? application.subtitle || "" : "",
      description: isPortuguese ? "" : application.event_description || "",
      description_pt: isPortuguese ? application.event_description || "" : "",
      picture: pictureUrl ?? application.main_picture ?? "",
      start_time: startTime,
      finish_time: finishTime,
      price:
        application.price === "0"
          ? ""
          : isPortuguese
          ? ""
          : application.price || "",
      price_pt:
        application.price === "0"
          ? ""
          : isPortuguese
          ? application.price || ""
          : "",
      organizer_id: null,
      main_tag: mainTagId,
      venue_name: "",
      venue_address: "",
      venue_city: "",
      venue_location_lat: null,
      venue_location_long: null,
      venue_description: "",
      published: false,
      cancelled: false,
      is_recommended: false,
      registration_link: "",
    };
  };

  // Extract organizer information from application
  const extractOrganizerInfo = () => {
    if (!application) return undefined;

    // The columns are `string | null`; `ApplicationOrganizerInfo` declares
    // `string | undefined`. Coerced here rather than widening the shared type:
    // every consumer tests truthiness or falls back with `|| ""`, so the two
    // are behaviourally identical. Under the old `any` this never surfaced.
    return {
      organiserName: application.organiser_name ?? undefined,
      organiserPhone: application.organiser_phone ?? undefined,
      organiserEmail: application.organiser_email ?? undefined,
      organiserMessage: application.organiser_message ?? undefined,
      organiserLogo: application.organiser_logo ?? undefined,
    };
  };

  // Extract venue information from application
  const extractVenueInfo = () => {
    if (!application) return undefined;

    return {
      venue_name: application.event_venue_location || "",
      venue_location: application.google_maps || "",
      venue_address: application.venue_location_address || "",
      venue_city: application.venue_village || "",
      venue_description: application.venue_description || "",
      venue_phone: application.venue_phone || "",
      venue_website: application.venue_website || "",
    };
  };

  // The fields the application itself never carried. Derived from the row's
  // own nulls, not read from the metadata blob — the persisted metadata shape
  // has no missing-fields member, and the row is the authority anyway.
  const missingFields = (app: ApplicationRow): string[] => {
    const missing: string[] = [];
    if (isBlank(app.event_title)) missing.push(t("events.event_title"));
    if (isBlank(app.starting_date)) missing.push(t("events.start_date"));
    if (isBlank(app.starting_time)) missing.push(t("events.time"));
    if (isBlank(app.ending_time)) missing.push(t("events.end_date"));
    if (isBlank(app.main_picture)) missing.push(t("events.event_picture"));
    return missing;
  };

  const renderProvenance = (app: ApplicationRow) => {
    if (!app.source) return null;

    const metadata = readImportMetadata(app.import_metadata);
    const missing = missingFields(app);
    const isSafeSourceUrl = isHttpsUrl(app.source_url);

    return (
      <Stack gap="xs" style={{ padding: 24, paddingBottom: 0 }}>
        <Alert
          icon={<IconInfoCircle size={18} />}
          color="blue"
          title={t("applications.imported_banner_title", {
            source: app.source,
          })}
        >
          <Stack gap={4}>
            {isSafeSourceUrl && app.source_url ? (
              <Anchor
                href={app.source_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("applications.view_source")}
              </Anchor>
            ) : (
              app.source_url && <Text size="sm">{app.source_url}</Text>
            )}
            {metadata.capturedAt && (
              <Text size="sm">
                {t("applications.captured_at", {
                  date: formatDateTime(metadata.capturedAt),
                })}
              </Text>
            )}
            {metadata.model && (
              <Text size="sm">
                {t("applications.extracted_by", { model: metadata.model })}
              </Text>
            )}
          </Stack>
        </Alert>

        {app.import_status === "failed" && (
          <Alert
            icon={<IconAlertTriangle size={18} />}
            color="red"
            title={t("applications.failed_title")}
          >
            <Stack gap={4}>
              {metadata.error && <Text size="sm">{metadata.error}</Text>}
              <Text size="sm">{t("applications.failed_body")}</Text>
            </Stack>
          </Alert>
        )}

        {/* The discard reason is mapped to fixed strings, never rendered raw:
            the blob comes from a pipeline that starts at an untrusted page.
            Informs, does not block — the form below still converts the row. */}
        {app.import_status === "discarded" && (
          <Alert
            icon={<IconArchive size={18} />}
            color="gray"
            title={t("applications.discarded_title")}
          >
            <Stack gap={4}>
              <Text size="sm">
                {metadata.discardReason === "elapsed"
                  ? t("applications.discard_reason_elapsed")
                  : t("applications.discard_reason_unknown")}
              </Text>
              <Text size="sm">{t("applications.discarded_body")}</Text>
            </Stack>
          </Alert>
        )}

        {metadata.warnings.length > 0 && (
          <Alert
            icon={<IconAlertTriangle size={18} />}
            color="orange"
            title={t("applications.warnings_title")}
          >
            <List size="sm">
              {metadata.warnings.map((warning, index) => (
                <List.Item key={`warning-${index}`}>{warning}</List.Item>
              ))}
            </List>
          </Alert>
        )}

        {metadata.inferredFields.length > 0 && (
          <Alert
            icon={<IconInfoCircle size={18} />}
            color="yellow"
            title={t("applications.inferred_title")}
          >
            <List size="sm">
              {metadata.inferredFields.map((field, index) => (
                <List.Item key={`inferred-${index}`}>{field}</List.Item>
              ))}
            </List>
          </Alert>
        )}

        {missing.length > 0 && (
          <Alert
            icon={<IconAlertTriangle size={18} />}
            color="gray"
            title={t("applications.missing_title")}
          >
            <List size="sm">
              {missing.map((field, index) => (
                <List.Item key={`missing-${index}`}>{field}</List.Item>
              ))}
            </List>
          </Alert>
        )}
      </Stack>
    );
  };

  const renderNotFound = (messageKey: string) => (
    <Flex direction="column" style={{ padding: 24 }}>
      <Alert
        icon={<IconAlertTriangle size={18} />}
        color="red"
        title={t(messageKey)}
      >
        <Anchor component={Link} to="/applications">
          {t("navigation.applications")}
        </Anchor>
      </Alert>
    </Flex>
  );

  const eventData = constructEventFromApplication();
  const organizerInfo = extractOrganizerInfo();
  const venueInfo = extractVenueInfo();

  if (isLoadingApplication) {
    return (
      <Flex style={{ padding: 24 }}>
        <Text>{t("common.loading")}</Text>
      </Flex>
    );
  }

  // The query uses a single-row read, which THROWS on zero rows rather than
  // resolving to null. Gating on `!application` alone left a deep link to a
  // deleted application on the loader forever. Only the code is used to pick
  // the message; the raw Supabase error text is deliberately not rendered.
  if (isApplicationError) {
    return renderNotFound(
      readErrorCode(applicationError) === "PGRST116"
        ? "applications.not_found"
        : "applications.load_error"
    );
  }

  if (!application) {
    return renderNotFound("applications.not_found");
  }

  // A processed application renders NO form at all. Disabling a submit inside
  // a form the curator can still edit invites the question of what the edits
  // did (nothing).
  if (application.processed) {
    return (
      <Flex direction="column" style={{ height: "100%" }}>
        {renderProvenance(application)}
        <Stack gap="xs" style={{ padding: 24 }}>
          <Alert
            icon={<IconCircleCheck size={18} />}
            color="green"
            title={t("applications.processed_title")}
          >
            <Text size="sm">{t("applications.processed_body")}</Text>
          </Alert>
          {createdEvents.length > 0 && (
            <Alert
              icon={<IconInfoCircle size={18} />}
              color="blue"
              title={t("applications.created_events_title")}
            >
              <List size="sm">
                {createdEvents
                  .filter((createdEvent) => !!createdEvent.uid)
                  .map((createdEvent) => (
                    <List.Item key={createdEvent.id}>
                      <Anchor
                        component={Link}
                        to={`/Events/${createdEvent.uid}`}
                        aria-label={t("applications.open_created_event")}
                      >
                        {createdEvent.title ||
                          createdEvent.title_pt ||
                          formatDateTime(createdEvent.start_time)}
                      </Anchor>
                    </List.Item>
                  ))}
              </List>
            </Alert>
          )}
        </Stack>
      </Flex>
    );
  }

  // A row with no extraction yet is not a row whose extraction returned
  // nothing, and an empty form cannot tell a curator which one they are looking
  // at (spec 10.2). So pending renders as waiting and renders NO form at all —
  // the banner, the source link and the screenshot are what exist, and they are
  // all shown. Positioned after the processed arm, because a processed row is
  // processed whatever its import status, and before the picture-conversion
  // loader, because waiting on a conversion would hide the state behind a
  // spinner for a row that has no fields to convert anything for.
  if (application.import_status === "pending") {
    return (
      <Flex direction="column" style={{ height: "100%" }}>
        {renderProvenance(application)}
        <Stack gap="xs" style={{ padding: 24 }}>
          <Alert
            icon={<IconClock size={18} />}
            color="blue"
            title={t("applications.pending_title")}
          >
            <Text size="sm">{t("applications.pending_body")}</Text>
          </Alert>
          {isHttpsUrl(application.main_picture) && application.main_picture && (
            <Stack gap={4}>
              <Text size="sm" fw={500}>
                {t("applications.screenshot_title")}
              </Text>
              <Image
                src={application.main_picture}
                alt={t("applications.screenshot_title")}
                fit="contain"
                mah={480}
              />
            </Stack>
          )}
        </Stack>
      </Flex>
    );
  }

  if (isConvertingPdf || pictureUrl === null) {
    return (
      <Flex style={{ padding: 24 }}>
        <Text>{t("common.loading")}</Text>
      </Flex>
    );
  }

  if (!eventData) {
    return (
      <Flex style={{ padding: 24 }}>
        <Text>{t("common.error")}</Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column" style={{ height: "100%" }}>
      {renderProvenance(application)}
      <EventForm
        isNew={false}
        event={eventData}
        venues={venues || []}
        organizers={organizers || []}
        tags={tags || []}
        currOrganizerId={undefined}
        organizerVenues={[]}
        isApplicationEdit={true}
        applicationOrganizerInfo={organizerInfo}
        applicationVenueLocation={venueInfo}
        applicationContext={{
          id: application.id,
          source: application.source,
          sourceUrl: application.source_url,
          sourceUid: application.source_uid,
          // Read through the same narrowing view `renderProvenance` uses, never
          // off `application.import_metadata` directly. One narrowing point:
          // `readRect` is what guarantees four finite numbers or null, so a
          // malformed measurement seeds nothing instead of seeding nonsense.
          artworkRect: readImportMetadata(application.import_metadata)
            .artworkRect,
        }}
        weekly={
          application.weekly_event ===
          "Yes - for this month / Sim - para este mês"
        }
      />
    </Flex>
  );
};
